import { Map as ImmutableMap } from 'immutable';
import { isJourneyReport, isMultiConfigReport, isConfigReport, isUnifiedEventsReport, isRelationalReport } from 'reporting-data/tsTypes/reportTypes';
import { getConfigsIncludingPrimary } from 'reporting-data/report/multiConfigReportGetters';
import { getReportSchemaReportType } from 'reporting-data/report/unifiedEventsReportGetters';
import { getStandardFieldType } from 'reporting-ui-components/lib/report-strategies/shared/utils/reportGetters';
import { STANDARD_FIELD_TYPES } from 'reporting-ui-components/lib/report-strategies/shared/constants/fieldTypes';
import { orderByFrequency } from '../relevancyScoreUtils';
import { getDataTypeFamiliesFromRelationalReport } from '../reportUtils/relationalReportUtils';
import { getDataTypeFamiliesFromJourneyReport } from '../reportUtils/journeyUtils';
import { getDataTypeFamiliesByKeyword, getDataTypeFamiliesForDataSource } from './dataTypeFamilyUtils';
const deriveDataTypeFamiliesFromProperties = report => {
  const metrics = getStandardFieldType(report, STANDARD_FIELD_TYPES.METRICS, []);
  const dimensions = getStandardFieldType(report, STANDARD_FIELD_TYPES.DIMENSIONS, []);
  const allProperties = metrics.concat(dimensions);
  return allProperties.reduce((acc, {
    property
  }) => getDataTypeFamiliesByKeyword(property).concat(acc), []);
};

// If a data source doesn't fall into a family, it'll be identified via:
// - dataSourceId for framework objects (equivalent to objectTypeId), digests, & virtual data sources
// - event name for events (id isn't consistent across environments)
// - data type name for unified data types
export const deriveDataTypeFamiliesFromDataSources = report => {
  if (isJourneyReport(report)) {
    const journeyReport = report.toJS();
    return getDataTypeFamiliesFromJourneyReport(journeyReport);
  }
  if (isRelationalReport(report)) {
    return getDataTypeFamiliesFromRelationalReport(report);
  }
  if (isMultiConfigReport(report)) {
    const configsMap = getConfigsIncludingPrimary(report);
    return configsMap.valueSeq().flatMap(config => {
      const dataType = getStandardFieldType(ImmutableMap({
        config
      }), STANDARD_FIELD_TYPES.PRIMARY_DATA_SOURCE_ID);
      return getDataTypeFamiliesForDataSource(dataType);
    }).toArray();
  }

  // Using report schema report type to identify unified events
  if (isConfigReport(report) && isUnifiedEventsReport(report)) {
    const reportType = getReportSchemaReportType(report);
    return [reportType];
  }
  return getStandardFieldType(report, STANDARD_FIELD_TYPES.ALL_DATA_SOURCE_IDS, []).flatMap(dataSourceId => getDataTypeFamiliesForDataSource(dataSourceId));
};
export const getDataTypeFamiliesFromReport = report => {
  const dataTypeFamiliesFromProperties = deriveDataTypeFamiliesFromProperties(report);
  const dataTypeFamiliesFromSources = deriveDataTypeFamiliesFromDataSources(report);
  const mergedGroups = dataTypeFamiliesFromSources.concat(dataTypeFamiliesFromProperties);
  return orderByFrequency(mergedGroups);
};
export const getDataTypeFamiliesFromReports = reports => {
  const dataTypeFrequencies = reports.reduce((memo, report) => {
    const dataTypeFamilies = getDataTypeFamiliesFromReport(report);
    dataTypeFamilies.forEach(dataTypeFamily => {
      memo[dataTypeFamily] = (memo[dataTypeFamily] || 0) + 1;
    });
    return memo;
  }, {});
  return Object.keys(dataTypeFrequencies).sort((dataTypeA, dataTypeB) => dataTypeFrequencies[dataTypeB] - dataTypeFrequencies[dataTypeA]);
};
export const getDataTypeFamiliesFromDashboardTemplate = template => {
  const widgets = template.get('widgetTemplates');
  const reportTemplates = widgets.map(widget => widget.get('reportTemplate'));
  return getDataTypeFamiliesFromReports(reportTemplates);
};